import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react'

import axios from 'axios'
import PropTypes from 'prop-types'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'

const SearchSubscriptionContext = createContext(null)

export function SearchSubscriptionProvider({
  children,
  selectedFilters = EMPTY_OBJECT,
  pageCategory = '',
  currentTotalCount = EMPTY_OBJECT,
}) {
  const [loading, setLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const handleChange = useCallback((e) => {
    setInputValue(e.target.value)
  }, [])

  const getPayload = (filters) => {
    const {
      pricing,
      page,
      modelType,
      fileTypes,
      priceRange,
      polygonRange,
      designers,
      keywords,
      ...otherFilters
    } = filters

    const payload = {
      page,
      ...pricing,
      ...modelType,
      ...designers,
      ...otherFilters,
      keywords: keywords || pageCategory || '',
      file_types: fileTypes,
      price_range: priceRange,
      polygon_range: polygonRange,
    }

    return payload
  }

  const onSubscribeSuccess = ({ data: { message } }) => {
    setLoading(false)
    setInputValue('')

    window.showNotification(message, {
      type: 'success',
      autohide: true,
    })

    window.customEventTracker({
      name: 'search_subscription_success',
      userId: window.pageConfig?.currentUserId || '',
      event_data: {},
    })
  }

  const onSubscribeFailure = (err) => {
    setLoading(false)

    window.showNotification(err, {
      type: 'error',
      autohide: true,
    })

    window.customEventTracker({
      name: 'search_subscription_failure',
      userId: window.pageConfig?.currentUserId || '',
      event_data: {},
    })
  }

  const handleSubmit = useCallback(
    (e) => {
      if (e) e.preventDefault()
      setLoading(true)

      window.customEventTracker({
        name: 'search_subscription_submit',
        userId: window.pageConfig?.currentUserId || '',
        event_data: {},
      })

      axios
        .post('/api/internal/profile/search_subscriptions/', {
          authenticity_token: window.getToken(),
          search_description: inputValue,
          filters: getPayload(selectedFilters),
          items_count: currentTotalCount,
          href: window.location.href,
        })
        .then(onSubscribeSuccess)
        .catch(onSubscribeFailure)
    },
    [inputValue, selectedFilters, currentTotalCount]
  )

  const contextValue = useMemo(
    () => ({
      loading,
      inputValue,
      handleChange,
      handleSubmit,
    }),
    [loading, inputValue, handleChange, handleSubmit]
  )

  return (
    <SearchSubscriptionContext.Provider value={contextValue}>
      {children}
    </SearchSubscriptionContext.Provider>
  )
}

SearchSubscriptionProvider.propTypes = {
  children: PropTypes.node.isRequired,
  selectedFilters: PropTypes.object,
  pageCategory: PropTypes.string,
  currentTotalCount: PropTypes.object,
}

export function useSearchSubscription() {
  const context = useContext(SearchSubscriptionContext)
  if (!context) {
    throw new Error(
      'useSearchSubscription should be used within a SearchSubscriptionProvider'
    )
  }
  return context
}
